/**
 * Compact one-line coverage strip for the Translations Tab. Replaces the
 * old `CoverageCard` and the earlier 4-card grid.
 *
 * Shows how many of each entity type (template / screens / fields /
 * options) have their keys on UILM, plus a small footer summarising
 * missing / mismatched / orphan counts. Detailed per-key lists live in
 * the dialog opened by the "View details" button.
 */

import { useMemo, useState } from 'react';
import { Eye } from 'lucide-react';
import { Badge } from '@/components/ui-kit/badge';
import { Button } from '@/components/ui-kit/button';
import type { KeyOwnerType, KeyPlanEntry } from '../../lib/build-template-keys';
import type { TemplateUilmDiff } from '../../hooks/use-template-uilm-diff';
import type { UilmTranslationJob } from '../../types/uilm-translation-job.types';
import { TranslationTabDetailsDialog } from './translation-tab-details-dialog';

interface TranslationTabStatsProps {
  diff: TemplateUilmDiff;
  /**
   * Most-recent auto-translate job (any status). Drives the "Last DE"
   * chip on the right end of the coverage strip. `null`/`undefined` →
   * "not yet auto-translated".
   */
  latestJob?: UilmTranslationJob | null;
  /** Refetch trigger called after a successful mismatch reconciliation. */
  onReconciled: () => void;
}

export function TranslationTabStats({ diff, latestJob, onReconciled }: TranslationTabStatsProps) {
  const [open, setOpen] = useState(false);

  // Group localPlan entries by ownerType so we can show "5/7 fields on UILM".
  const coverage = useMemo(
    () => groupCoverage(diff.localPlan, diff.missing),
    [diff.localPlan, diff.missing]
  );

  const totalLocal = diff.localPlan.length;
  const totalOnUilm = totalLocal - diff.missing.length;

  return (
    <>
      <div className="rounded-lg border bg-muted/30 px-3 py-2.5 sm:px-4 sm:py-3 space-y-3">
        {/* Coverage row — chips wrap on mobile; the running total + "View
            details" button drop to a second row when the chips fill the width. */}
        <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
          <span className="text-xs font-medium text-globe-grey">Coverage</span>
          <CoverageChip label="Template" {...coverage.template} />
          <CoverageChip label="Screens" {...coverage.screen} />
          <CoverageChip label="Fields" {...coverage.field} />
          <CoverageChip label="Options" {...coverage.option} />

          {/* "ml-auto" only on sm+ — on phones we want the chip to sit
              inline with the others instead of being pushed alone to a row. */}
          <span className="sm:ml-auto text-xs text-globe-grey font-mono">
            {totalOnUilm}/{totalLocal} on UILM
          </span>

          <Button
            variant="primaryOutline"
            size="sm"
            onClick={() => setOpen(true)}
            className="ml-auto sm:ml-0 hidden md:inline-flex shrink-0"
          >
            <Eye className="h-3.5 w-3.5 mr-1.5" />
            View details
          </Button>
        </div>

        {/* Diff footer — bullets wrap with the rest of the stats so the
            row doesn't overflow horizontally on phones. */}
        <div className="flex items-center gap-x-2 sm:gap-x-3 gap-y-1 flex-wrap mt-2 text-xs text-globe-grey">
          <span>
            <span className="font-mono text-amber-600">{diff.missing.length}</span> missing
          </span>
          <span className="text-globe-grey/60">•</span>
          <span>
            <span className="font-mono text-selise-blue">{diff.mismatched.length}</span> mismatches
          </span>
          <span className="text-globe-grey/60">•</span>
          <span>
            <span className="font-mono">{diff.orphans.length}</span> orphans
          </span>
          <span className="text-globe-grey/60">•</span>
          <span>
            <span className="font-mono text-poly-green">{diff.inSync.length}</span> in sync
          </span>
          <LastDeChip job={latestJob} />
        </div>

        <div className="md:hidden flex items-center justify-center">
          <Button variant="primaryOutline" size="sm" onClick={() => setOpen(true)} className="">
            <Eye className="h-3.5 w-3.5 mr-1.5 " />
            View details
          </Button>
        </div>
      </div>

      <TranslationTabDetailsDialog
        open={open}
        onOpenChange={setOpen}
        diff={diff}
        onReconciled={onReconciled}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Private — one coverage chip ("Screens 3/5")
// ---------------------------------------------------------------------------

interface CoverageChipProps {
  label: string;
  total: number;
  onUilm: number;
}

function CoverageChip({ label, total, onUilm }: CoverageChipProps) {
  if (total === 0) {
    return (
      <Badge variant="outline" className="text-xs text-globe-grey">
        {label}: —
      </Badge>
    );
  }
  const complete = onUilm === total;
  return (
    <Badge
      variant="secondary"
      className={
        complete
          ? 'text-xs bg-primary text-primary-foreground hover:bg-primary/90'
          : 'text-xs bg-red-100 text-amber-800 hover:bg-red-200'
      }
    >
      {label}:{' '}
      <span className="font-mono ml-1">
        {onUilm}/{total}
      </span>
    </Badge>
  );
}

// ---------------------------------------------------------------------------
// Private — "Last DE" chip (state-aware: running / completed / failed / never)
// ---------------------------------------------------------------------------

/**
 * Renders as part of the diff-data row, so it follows the same shape as
 * the other items: a leading `•` separator + a `<span>` with a label and
 * a font-mono coloured value. Picks color + value text from the job
 * status so the row reads at a glance:
 *
 *   12 missing • 3 mismatches • 0 orphans • 41 in sync • Last DE: 12m ago
 */
function LastDeChip({ job }: { job?: UilmTranslationJob | null }) {
  const { value, valueClass, title } = describeJob(job);
  return (
    <>
      <span className="text-globe-grey/60">•</span>
      <span title={title}>
        Last DE: <span className={`font-mono ${valueClass}`}>{value}</span>
      </span>
    </>
  );
}

function describeJob(job?: UilmTranslationJob | null): {
  value: string;
  valueClass: string;
  title?: string;
} {
  if (!job) {
    return { value: 'never', valueClass: '' };
  }
  if (job.status === 'running') {
    return { value: 'translating…', valueClass: 'text-selise-blue' };
  }
  if (job.status === 'failed') {
    return {
      value: 'last attempt failed',
      valueClass: 'text-destructive',
      title: job.errorMessage ?? 'Auto-translation failed',
    };
  }
  // completed — relative time, full timestamp on hover.
  const ts = job.completedAt ?? job.updatedAt;
  return {
    value: formatRelativeTime(ts),
    valueClass: 'text-poly-green',
    title: new Date(ts).toLocaleString(),
  };
}

/**
 * Coarse "N ago" formatter. Picks the largest sensible unit and rounds —
 * we only need this for one timestamp on one chip, not full Intl support.
 * Falls back to a localized date when older than ~30 days.
 */
function formatRelativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '—';
  const diffMs = Date.now() - then;
  if (diffMs < 0) return 'just now'; // clock skew — treat as just-now
  const sec = Math.floor(diffMs / 1000);
  if (sec < 60) return 'just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day}d ago`;
  return new Date(iso).toLocaleDateString();
}

// ---------------------------------------------------------------------------
// Private — coverage computation
// ---------------------------------------------------------------------------

function groupCoverage(localPlan: KeyPlanEntry[], missing: KeyPlanEntry[]) {
  const totals: Record<KeyOwnerType, number> = {
    template: 0,
    screen: 0,
    field: 0,
    option: 0,
  };
  const missingByType: Record<KeyOwnerType, number> = {
    template: 0,
    screen: 0,
    field: 0,
    option: 0,
  };

  for (const e of localPlan) totals[e.ownerType] += 1;
  for (const e of missing) missingByType[e.ownerType] += 1;

  return {
    template: { total: totals.template, onUilm: totals.template - missingByType.template },
    screen: { total: totals.screen, onUilm: totals.screen - missingByType.screen },
    field: { total: totals.field, onUilm: totals.field - missingByType.field },
    option: { total: totals.option, onUilm: totals.option - missingByType.option },
  };
}
